<template>
  <div class="sp-number-slider" :class="classModifiers">
    <!-- Hidden from assistive technology: the slider announces the label and value itself. -->
    <div v-if="sliderOnly" class="sp-number-slider__header" aria-hidden="true">
      <span v-if="label" class="sp-number-slider__label" part="label">{{ label }}</span>
      <span class="sp-number-slider__value">{{ displayValue }}</span>
    </div>
    <div v-else class="sp-number-slider__header">
      <label v-if="label" class="sp-number-slider__label" part="label" :for="inputId">{{ label }}</label>
      <input
        :id="inputId"
        class="sp-number-slider__input"
        part="input"
        type="number"
        inputmode="numeric"
        :min="minValue"
        :max="maxValue"
        :step="rangeStep"
        :value="currentValue"
        :disabled="disabled"
        :data-testid="inputTestId"
        :aria-label="label"
        @keydown="handleInputKeydown"
        @input.stop="handleInputStep"
        @change.stop="handleInputChange"
      />
    </div>
    <div class="sp-number-slider__track">
      <sp-input-range-slider
        v-preserve-properties:min="rangeMin"
        v-preserve-properties:max="rangeMax"
        v-preserve-properties:step="rangeStep"
        v-preserve-properties:value="rangeValue"
        class="sp-number-slider__range"
        part="range"
        :show-ticks="hasSnapValues"
        :disabled="disabled"
        :accessible-name="label"
        :value-text="displayValue"
        :data-testid="rangeTestId"
        @input="handleSliderInput"
        @change="handleSliderChange"
      ></sp-input-range-slider>
      <div v-if="showBounds" class="sp-number-slider__bounds" aria-hidden="true">
        <span>{{ minValue }}</span>
        <span>{{ maxValue }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { computed, ref, toRef, useAttrs, watch } from "vue";
import { NumberSliderVariant, props as numberSliderProps } from "./props.js";
import { useCommittedModel } from "#ce/composables/useCommittedModel";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { useSnapValues } from "#ce/composables/useSnapValues";
import { toBoolean, toNumber, trimToUndefined } from "#ce/utils/props";
import { formAssociatedHost } from "#ce/framework/utils";

// sp-number-slider is a genuine form control: submit a value, match :disabled, respond to
// <fieldset disabled>, like the sp-input-range-slider it wraps.
defineOptions(formAssociatedHost());

const props = defineProps(numberSliderProps);

defineEmits(["input", "change"]);
const { emitBubbling } = useHostEmit();

const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

// Instance-unique id for the label/input association, matching SpRadioGroup's
// pattern: a random suffix avoids cross-instance collisions without module-level
// state, and Vue's useId() can't be used here (each custom element mounts as its
// own app, so every instance would get the same "v-0").
const inputId = `sp-number-slider-input-${Math.random().toString(36).slice(2, 10)}`;

// trimToUndefined so an empty attribute falls back to the default instead of
// Number("") === 0 — a zero step would make the normalize() grid math NaN.
const numericMin = computed(() => toNumber(trimToUndefined(props.min), 1));
const numericMax = computed(() => toNumber(trimToUndefined(props.max), 150));
const numericStep = computed(() => {
  const step = toNumber(trimToUndefined(props.step), 1);
  return step > 0 ? step : 1;
});

const { snapValues, hasSnapValues, nearestSnapIndex, nearestSnapValue, adjacentSnapValue } = useSnapValues(
  toRef(props, "values"),
);

const maxValue = computed(() => (hasSnapValues.value ? snapValues.value.at(-1) : numericMax.value));
const minValue = computed(() => (hasSnapValues.value ? snapValues.value[0] : numericMin.value));

// `input` fires on every adjustment (each slider drag tick); `change` fires
// only on commit — thumb release or a numeric-input edit — so consumers that
// fetch per change (the finder) aren't hammered while the thumb is dragged.
// The `value` attribute seeds the slider and names the value a reset restores; the `value`
// property is the live position. A declared prop would install a non-configurable host accessor
// that returns the seed forever.
const attrs = useAttrs();
const defaultValue = ref(attrs.value);
// Snapped like a property write, so an off-grid seed reads where the thumb sits.
const seedValue = computed(() => toSelectableValue(defaultValue.value));
const { model, update, commit, set } = useCommittedModel(seedValue, {
  onUpdate: (value) => emitBubbling("input", value),
  onCommit: (value) => emitBubbling("change", value),
});

watch(
  () => attrs.value,
  (value) => (defaultValue.value = value),
);

// An unset model reads as the largest selectable value, and that one value is what the live
// `value` property reports, the inner controls render, and the form submits.
const currentValue = computed(() => model.value ?? maxValue.value);
const displayValue = computed(() => props.formatValue?.(currentValue.value) ?? String(currentValue.value));

const sliderOnly = computed(() => props.variant === NumberSliderVariant.SLIDER_ONLY);
const showBounds = computed(() => toBoolean(props.showBounds));
const rangeTestId = computed(() => (sliderOnly.value ? props.inputTestId : undefined));

const { exposeProperties } = useExpose();

exposeProperties({
  value: {
    configurable: true,
    get: () => currentValue.value,
    // Normalized like a typed commit, so the form value stays inside the slider's bounds.
    set: (value) => set(toSelectableValue(value)),
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

// The inner slider sits in the shadow root, where the outer form cannot see it, so the element
// submits through ElementInternals. `change` is already reported on commit above.
const { mirrorFormValue, onFormReset } = useForm();
const formValue = computed(() => String(currentValue.value ?? ""));

mirrorFormValue(formValue);

onFormReset(() => set(seedValue.value));

// With snap values the inner slider works on the list index — one tick per
// entry — so uneven gaps (…25, 50, 100…) each take one step of travel.
const rangeMin = computed(() => (hasSnapValues.value ? 0 : numericMin.value));
const rangeMax = computed(() => (hasSnapValues.value ? snapValues.value.length - 1 : numericMax.value));
const rangeStep = computed(() => (hasSnapValues.value ? 1 : numericStep.value));
const rangeValue = computed(() => (hasSnapValues.value ? nearestSnapIndex(currentValue.value) : currentValue.value));

const classModifiers = computed(() => {
  const modifiers = {};

  if (disabled.value) {
    modifiers["--disabled"] = true;
  }

  if (!hasSnapValues.value) {
    modifiers["--continuous"] = true;
  }

  return modifiers;
});

function normalize(value) {
  if (hasSnapValues.value) {
    return nearestSnapValue(value);
  }

  // Align to the step grid (anchored at min, like the native stepper), then clamp.
  const stepped = numericMin.value + Math.round((value - numericMin.value) / numericStep.value) * numericStep.value;
  return Math.min(numericMax.value, Math.max(numericMin.value, stepped));
}

function toSelectableValue(value) {
  const numeric = toNumber(trimToUndefined(value));

  return numeric === undefined ? undefined : normalize(numeric);
}

function handleInputChange(event) {
  const value = toNumber(trimToUndefined(event.target.value));

  if (value === undefined) {
    // Restore the previous value when the field is cleared or non-numeric.
    event.target.value = currentValue.value;
    return;
  }

  const normalized = normalize(value);
  event.target.value = normalized;
  commit(normalized);
}

// With snap values, stepping must jump to the adjacent list entry (15 → 20),
// not by the input's uniform step attribute — a native number input can't
// express uneven steps. Arrow keys are intercepted here; spinner clicks are
// caught in handleInputStep below.
function handleInputKeydown(event) {
  if (hasSnapValues.value && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
    event.preventDefault();
    stepToAdjacent(event.key === "ArrowUp" ? 1 : -1, event.target);
  }
}

// A spinner-button click moves the value by one step and fires `input` as a
// plain Event with no `inputType` in Chrome and Safari, but as an InputEvent
// with inputType "insertReplacementText" in Firefox (all verified in-browser).
// (Arrow-key steps fire the same event but never reach here: handleInputKeydown
// intercepts them.) Typing (`insertText`) and pasting (`insertFromPaste`) carry
// their own inputType; neither may be misread as a spinner step (e.g. pasting
// 26 over 25), so they bail here and are left for the change commit to snap.
// "insertReplacementText" is also autofill's inputType, which the ±1 delta
// check below screens out: a whole-value replacement rarely lands exactly one
// native step away, and one that does just steps to the adjacent snap stop.
function handleInputStep(event) {
  if (!hasSnapValues.value || (event.inputType && event.inputType !== "insertReplacementText")) {
    return;
  }

  const diff = toNumber(trimToUndefined(event.target.value)) - currentValue.value;

  if (Math.abs(diff) === 1) {
    stepToAdjacent(diff, event.target);
  }
}

function stepToAdjacent(direction, input) {
  const next = adjacentSnapValue(currentValue.value, direction);

  if (next === undefined) {
    input.value = currentValue.value;
    return;
  }

  input.value = next;
  commit(next);
}

function handleSliderInput({ detail }) {
  const [value] = detail;
  update(hasSnapValues.value ? snapValues.value[value] : value);
}

function handleSliderChange({ detail }) {
  const [value] = detail;
  commit(hasSnapValues.value ? snapValues.value[value] : value);
}
</script>

<style>
:host {
  display: block;

  --label-font: var(--sp-comp-number-slider-label-font, var(--sp-sys-typescale-small-short));
  --label-color: var(--sp-comp-number-slider-label-color, var(--sp-sys-color-on-surface));
  --input-font: var(--sp-comp-number-slider-input-font, var(--sp-sys-typescale-body-short));
  --input-color: var(--sp-comp-number-slider-input-color, var(--sp-sys-color-on-surface));
  --input-bg-color: var(--sp-comp-number-slider-input-bg-color, var(--sp-sys-form-field-background-color));
  --input-border-color: var(--sp-comp-number-slider-input-border-color, var(--sp-sys-form-field-border-color));
  --input-border-radius: var(--sp-comp-number-slider-input-border-radius, var(--sp-sys-shape-control));
  --input-padding: var(--sp-comp-number-slider-input-padding, var(--sp-ref-scale-6) var(--sp-ref-scale-8));
  --number-slider-input-width: var(--sp-comp-number-slider-input-width, var(--sp-ref-scale-64));
  --gap: var(--sp-comp-number-slider-gap, var(--sp-ref-scale-8));
  --value-font: var(--sp-comp-number-slider-value-font, var(--sp-sys-typescale-body-semi-bold-short));
  --value-color: var(--sp-comp-number-slider-value-color, var(--sp-sys-color-primary));
  --bounds-font: var(--sp-comp-number-slider-bounds-font, var(--sp-sys-typescale-small-short));
  --bounds-color: var(--sp-comp-number-slider-bounds-color, var(--sp-sys-color-on-surface));
}

:host(:disabled) {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>

<style scoped lang="scss">
.sp-number-slider {
  display: flex;
  flex-direction: column;
  gap: var(--gap);

  &__label {
    font: var(--label-font);
    color: var(--label-color);
  }

  &__input {
    font: var(--input-font);
    color: var(--input-color);
    background-color: var(--input-bg-color);
    border: var(--sp-sys-border-width-default) solid var(--input-border-color);
    border-radius: var(--input-border-radius);
    padding: var(--input-padding);
    inline-size: var(--number-slider-input-width);
    box-sizing: border-box;
    // Keeps the input at the end of the row without a label, or when a long label wraps it onto its own line.
    margin-inline-start: auto;
    text-align: center;

    &:focus-visible {
      border-color: var(--sp-sys-form-field-focus-border-color);
      box-shadow: var(--sp-sys-form-field-focus-box-shadow);
      outline: var(--sp-sys-form-field-focus-outline);
      outline-offset: var(--sp-sys-form-field-focus-outline-offset);
    }
  }

  // Hides the spinner buttons in a continuous range, where a click moves one step and the buttons crowd
  // the digits. With snap values a click jumps to the adjacent entry, so they stay.
  &.--continuous &__input {
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      appearance: none;
      margin: 0;
    }
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--gap);
  }

  &__value {
    font: var(--value-font);
    color: var(--value-color);
  }

  // Inset like sp-input-range-slider's track, with each bound centered under one end of it.
  &__bounds {
    display: flex;
    justify-content: space-between;
    margin-block-start: var(--sp-ref-scale-2);
    margin-inline: calc(var(--sp-input-range-slider-thumb-size, var(--sp-ref-scale-24)) / 2);
    font: var(--bounds-font);
    color: var(--bounds-color);

    > span {
      display: flex;
      justify-content: center;
      width: 0;
      white-space: nowrap;
    }
  }

  &.--disabled &__input {
    pointer-events: none;
  }
}
</style>
